import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  Type,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { ReplaySubject, takeUntil } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { Comparison, WidgetGroup } from 'app/providers/bridge-api/dashboard.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

import { SavedView } from '../../models/saved-view.model';
import SidePaneItem from './SidePaneItem';

@EnableClassTelemetry()
@Component({
  selector: 'app-dashboard-side-pane',
  imports: [AppSharedModule],
  templateUrl: './dashboard-side-pane.component.html',
  styleUrls: ['./dashboard-side-pane.component.scss'],
})
export class DashboardSidePaneComponent<T extends WidgetGroup | Comparison | SavedView>
  implements OnChanges, AfterViewInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() headerText;
  @Input() headerIconTooltip;
  @Input() headerIconUrl;
  @Input() addButtonTooltip;
  @Input() hasDataSources;
  @Input() isEditable;
  @Input() isDisabled;
  @Input() isDashboardUnsaved;
  @Input() isDashboardLocked;
  @Input() isLoading;
  @Input() items: (WidgetGroup | Comparison | SavedView)[];
  @Input() emptyViewSvg = 'plus';
  @Input() emptyViewText;
  @Input() emptyViewTitle;
  @Input() ctaButtonText;
  @Input() searchPlaceholder;
  @Input() emptyMessage;
  @Input() itemType: 'widgetGroup' | 'comparison' | 'savedView';
  @Input() itemComponentType: Type<SidePaneItem<T>>;
  @Input() currentDashboardPageId: number;
  @Input() dashboardPages: [];
  @Input() sentimentScale: number;
  @Input() addButtonDisabled: boolean;
  @Input() addButtonDisabledTooltip: string;

  @Output() addButtonClick = new EventEmitter();
  @Output() itemAction = new EventEmitter<{
    itemType: string;
    action: string;
    item: T;
    data?: any;
  }>();

  @ViewChildren('itemTpl', { read: ViewContainerRef }) itemTpl: QueryList<ViewContainerRef>;

  selectedItem;
  fabTooltip;
  private itemSubs = new WeakMap();
  private unsub$ = new ReplaySubject<void>(1);
  private itemCmps = [];
  private itemExpansionStates = new Map<string, boolean>();
  private lastItemAction;

  searchText = '';
  private filteredItems: (WidgetGroup | Comparison | SavedView)[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.items ||
      changes.itemType ||
      changes.isEditable ||
      changes.isDisabled ||
      changes.isDashboardLocked ||
      changes.hasDataSources ||
      changes.dashboardPages ||
      changes.sentimentScale ||
      changes.currentDashboardPageId
    ) {
      this.updateFilteredItems();
      const hasItemsChange = !!changes.items;

      this.itemCmps.forEach((itemComp, index) => {
        if (hasItemsChange && index < this.filteredItems.length) {
          itemComp.setInput('item', this.filteredItems[index] as T);
        }
        itemComp.setInput('isEditable', this.isEditable);
        itemComp.setInput('isDisabled', this.isDisabled);
        itemComp.setInput('isDashboardLocked', this.isDashboardLocked);
        itemComp.setInput('hasDataSources', this.hasDataSources);
        itemComp.setInput('dashboardPages', this.dashboardPages);
        itemComp.setInput('sentimentScale', this.sentimentScale);
        itemComp.setInput('currentDashboardPageId', this.currentDashboardPageId);
      });
    }

    if (changes.isDisabled) {
      this.fabTooltip = {
        text: this.addButtonDisabled ? this.addButtonDisabledTooltip : this.addButtonTooltip,
      };

      if (changes.isDisabled?.currentValue) {
        this.fabTooltip = {
          text: this.isDashboardLocked
            ? translate('dashboard-side-pane-component.is-locked')
            : translate('dashboard-side-pane-component.is-disabled'),
        };
      }
    }

    if (changes.addButtonDisabled && !this.isDashboardLocked) {
      if (changes.addButtonDisabled?.currentValue) {
        this.fabTooltip = {
          text: this.addButtonDisabledTooltip,
        };
      } else {
        this.fabTooltip = {
          text: this.addButtonTooltip,
        };
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.createItemComponents());

    this.itemTpl.changes
      .pipe(takeUntil(this.unsub$))
      .subscribe(() => setTimeout(() => this.createItemComponents()));
  }

  ngOnDestroy(): void {
    this.unsub$.next();
  }

  private createItemComponents() {
    this.itemCmps = [];

    const itemsForRender = this.filteredItems;

    this.itemTpl.forEach((vcr, idx) => {
      vcr.clear();

      const item = itemsForRender[idx];

      if (!this.itemExpansionStates.has(item.id)) {
        this.itemExpansionStates.set(
          item.id,
          this.lastItemAction === 'create' || this.lastItemAction === 'clone'
        );
      }

      const itemCmp = vcr.createComponent(this.itemComponentType);
      itemCmp.setInput('item', item);
      itemCmp.setInput('isEditable', this.isEditable);
      itemCmp.setInput('isDisabled', this.isDisabled);
      itemCmp.setInput('isExpanded', this.itemExpansionStates.get(item.id));
      itemCmp.setInput('isDashboardLocked', this.isDashboardLocked);
      itemCmp.setInput('hasDataSources', this.hasDataSources);
      itemCmp.setInput('dashboardPages', this.dashboardPages);
      itemCmp.setInput('sentimentScale', this.sentimentScale);
      itemCmp.setInput('currentDashboardPageId', this.currentDashboardPageId);
      this.itemSubs.set(
        itemCmp,
        itemCmp.instance.itemAction.subscribe((event) => {
          if (event.action === 'expand') {
            this.itemExpansionStates.set(item.id, event.data);
            itemCmp.setInput('isExpanded', event.data);
            return;
          }

          this.lastItemAction = event.action;

          this.itemAction.emit({
            itemType: itemCmp.instance.itemType,
            action: event.action,
            item: itemCmp.instance.item,
            data: event.data,
          });
        })
      );

      itemCmp.onDestroy(() => {
        this.itemSubs.get(itemCmp).unsubscribe();
      });

      this.itemCmps.push(itemCmp);
    });

    this.lastItemAction = undefined;
  }

  addItem() {
    this.lastItemAction = 'create';
    this.addButtonClick.emit();
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    this.updateFilteredItems();
  }

  private updateFilteredItems(): void {
    if (!this.items?.length) {
      this.filteredItems = [];
      return;
    }

    if (this.itemType !== 'savedView') {
      this.filteredItems = this.items;
      return;
    }

    const query = this.searchText?.trim().toLowerCase();
    if (!query) {
      this.filteredItems = this.items;
      return;
    }

    this.filteredItems = this.items.filter((item) => {
      const savedView = item as SavedView;
      return savedView.name?.toLowerCase().includes(query);
    });
  }
}
